import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearEmployeeSession,
  employeeApi,
  getStoredEmployee,
  setEmployeeSession,
} from '@/lib/api';

const EmployeeAuthContext = createContext(null);

export function EmployeeAuthProvider({ children }) {
  const [employee, setEmployee] = useState(() => getStoredEmployee());
  const [token, setToken] = useState(() => localStorage.getItem('rota_employee_token'));
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem('rota_employee_token');
    if (!t) {
      setEmployee(null);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await employeeApi.get('/employee-auth/me');
      setEmployee(data);
      // Persist only the minimum required to render on refresh — see
      // ``EMPLOYEE_SESSION_WHITELIST`` in lib/api.js. Full profile (email,
      // phone, hourly_rate, monthly_salary) lives only in React state.
      setEmployeeSession(t, data);
      return data;
    } catch {
      clearEmployeeSession();
      setEmployee(null);
      setToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token && !employee) {
      refresh();
    }
  }, [token, employee, refresh]);

  const login = useCallback(async (slug, pin) => {
    const { data } = await employeeApi.post('/employee-auth/login', { slug, pin });
    // Full profile in React state; only whitelisted fields to localStorage.
    setEmployeeSession(data.access_token, data.employee);
    setEmployee(data.employee);
    setToken(data.access_token);
    return data.employee;
  }, []);

  const logout = useCallback(() => {
    clearEmployeeSession();
    setEmployee(null);
    setToken(null);
    window.location.href = '/portal/login';
  }, []);

  const value = useMemo(
    () => ({ employee, token, loading, login, logout, refresh }),
    [employee, token, loading, login, logout, refresh]
  );

  return <EmployeeAuthContext.Provider value={value}>{children}</EmployeeAuthContext.Provider>;
}

export function useEmployeeAuth() {
  const ctx = useContext(EmployeeAuthContext);
  if (!ctx) throw new Error('useEmployeeAuth must be used within EmployeeAuthProvider');
  return ctx;
}
